import React from 'react';
import { Box, Button, Typography, CircularProgress, Checkbox, IconButton } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import BottomSheetDialog from './mobile-bottom-sheet';
import { useSettings } from '../../../providers/settings-provider';

export type ProvisionTransactionRow = {
    id: string;
    date: string;
    amount: number | string;
    description: string;
    category: string;
    bank: string;
    type: 'real' | 'provision';
};

interface ProvisionTransactionsDisplayProps {
    open: boolean;
    provisionId?: number | null;
    transactions?: any[];
    banks?: any[];
    categories?: any[];
    onConfirm?: (trxGuids: string[]) => void;
    onClose?: () => void;
    isLoading?: boolean;
}

const EMPTY_ARRAY: any[] = [];

// Bottom-sheet phone equivalent of provision-transactions-display.tsx - same
// data-transform/default-all-selected logic, a tappable card list instead of
// DynamicTable's fixed-width columns.
const ProvisionTransactionsDisplayMobile: React.FC<ProvisionTransactionsDisplayProps> = ({
    open = false,
    provisionId,
    transactions = EMPTY_ARRAY,
    banks = EMPTY_ARRAY,
    categories = EMPTY_ARRAY,
    onConfirm,
    onClose,
    isLoading = false,
}) => {
    const { formatDate, formatAmount } = useSettings();
    const [data, setData] = React.useState<ProvisionTransactionRow[]>([]);
    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (transactions && transactions.length > 0) {
            const transformedData = transactions.map((transaction: any) => {
                let bankName = 'No Bank';
                if (transaction.bankAccountId) {
                    const bank = banks.find(b => b.id === transaction.bankAccountId);
                    if (bank) {
                        bankName = bank.bank && bank.name ? `${bank.bank} (${bank.name})` : bank.bank || bank.name || `Bank ID: ${transaction.bankAccountId}`;
                    } else {
                        bankName = `Bank ID: ${transaction.bankAccountId}`;
                    }
                } else if (transaction.bankName) {
                    bankName = transaction.bankName;
                } else if (transaction.bank?.name) {
                    bankName = transaction.bank.name;
                }

                let categoryName = 'Uncategorized';
                if (transaction.categoryId) {
                    const findCategory = (cats: any[]): any => {
                        for (const cat of cats) {
                            if (cat.id === transaction.categoryId) return cat;
                            if (cat.children && cat.children.length > 0) {
                                const found = findCategory(cat.children);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    const category = findCategory(categories);
                    categoryName = category?.name || `Category ID: ${transaction.categoryId}`;
                } else if (transaction.categoryName) {
                    categoryName = transaction.categoryName;
                } else if (transaction.category?.name) {
                    categoryName = transaction.category.name;
                }

                return {
                    id: transaction.id || transaction.guid || '',
                    date: transaction.date || transaction.transactionDate || '',
                    amount: transaction.amount || 0,
                    description: transaction.description || '',
                    category: categoryName,
                    bank: bankName,
                    type: transaction.type || 'real'
                };
            });
            setData(transformedData);
            setSelectedIds(transformedData.map(t => t.id));
        } else {
            setData([]);
            setSelectedIds([]);
        }
    }, [transactions, banks, categories]);

    const toggleSelected = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    return (
        <BottomSheetDialog
            open={open && !!provisionId}
            onClose={(_e, reason) => { if (reason === 'backdropClick') return; onClose?.(); }}
            disableEscapeKeyDown
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                <IconButton onClick={() => onClose?.()}><ArrowLeft size={22} /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Confirm Related Transactions</Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 1 }}>
                    Below are the transactions related to this provision. Select the transactions that you want to associate to this provision. These transactions will inherit the provision template's category, but you can edit them later if needed.
                </Typography>
                {data.map(row => (
                    <Box
                        key={row.id}
                        onClick={() => toggleSelected(row.id)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.25,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            background: selectedIds.includes(row.id) ? 'rgba(var(--color-primary-rgb), 0.08)' : 'var(--color-container-background)',
                            cursor: 'pointer',
                        }}
                    >
                        <Checkbox
                            checked={selectedIds.includes(row.id)}
                            size="small"
                            onChange={() => toggleSelected(row.id)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ p: 0 }}
                        />
                        <Box flex={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(new Date(row.date))}</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{formatAmount?.(Number(row.amount))}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 12, color: '#707070' }}>{row.description}</Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
            <Box sx={{ borderTop: '1px solid #e0e0e0', p: 1.5, position: 'sticky', bottom: 0, background: 'var(--color-page-background)' }}>
                <Button
                    onClick={() => onConfirm?.(selectedIds)}
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    sx={{ backgroundColor: 'var(--color-primary)', color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#009178' } }}
                >
                    {isLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} sx={{ color: '#fff' }} />
                            Processing...
                        </Box>
                    ) : 'Confirm'}
                </Button>
            </Box>
        </BottomSheetDialog>
    );
};

export default ProvisionTransactionsDisplayMobile;
